/* eslint-disable */
import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { showNotification } from "@mantine/notifications";
import { customAxios } from "lib/axios";


interface Department {
  _id: string;
  name: string;
}

interface Employee {
  id: string;
  name: string;
  position: string;
  department: Department;
  profileImage: string;
}

interface AttendanceRecord {
  employee: Employee;
  status: string; // Present, Absent, etc.
  checkIn: string;
  checkOut: string;
  breakMinutes: number;
  lateMinutes: number;
  productionHours: number;
  isLate: boolean;
  isOvertime: boolean;
  isHalfDay: boolean;
  day: string;
  notes:string;
  overtime: string;
  overtimeMinutes: string
}

interface Pagination {
  currentPage: number;
  totalPages: number;
  totalRecords: number;
  recordsPerPage: number;
}

interface Filters {
  startDate: string;
  endDate: string;
  department: string | null;
  status: string | null;
  sortBy: string | null;
}

interface AttendanceListResponse {
  success: boolean;
  data: {
    records: AttendanceRecord[];
    pagination: Pagination;
    filters: Filters;
  };
}

// --------------------
// API CALL
// --------------------
const getAttendanceAdminList = async (
  page: number = 1,
  limit: number = 10,
  status?: string | null,
  sortBy?: string,
  department?: string | null,
  startDate?: string | null,
  endDate?: string | null,
  search?: string,
  program?: string | null,
  sortOrder?: "asc" | "desc"
): Promise<AttendanceListResponse> => {
  const queryParams = new URLSearchParams();

  queryParams.append("page", page.toString());
  queryParams.append("limit", limit.toString());

  if (status) queryParams.append("status", status);
  if (sortBy) queryParams.append("sortBy", sortBy);
  if (department) queryParams.append("department", department);
  if (startDate) queryParams.append("startDate", startDate);
  if (endDate) queryParams.append("endDate", endDate);
  if (search) queryParams.append("search", search);
  if (program) queryParams.append("program", program);
  if (sortOrder) queryParams.append("sortOrder", sortOrder);

  const { data } = await customAxios.get<AttendanceListResponse>(
    `/attendance/admin/list?${queryParams.toString()}`
  );

  return data;
};


export const useGetAttendanceAdminList = (
  page: number = 1,
  limit: number = 10,
  status?: string | null,
  sortBy?: string,
  department?: string | null,
  startDate?: string | null,
  endDate?: string | null,
  search?: string,
  program?: string | null,
  sortOrder?: "asc" | "desc"
) =>
  useQuery<AttendanceListResponse, AxiosError>(
    ["admin-attendance-list", page, limit, status, sortBy, department, startDate, endDate, search, program, sortOrder],
    () =>
      getAttendanceAdminList(
        page,
        limit,
        status,
        sortBy,
        department,
        startDate,
        endDate,
        search,
        program,
        sortOrder
      ),
    {
      onError: (error) =>
        showNotification({
          title: "Error Loading Attendance Data",
          message:
            (error.response?.data as { message?: string })?.message ??
            "Failed to load attendance data",
          color: "red",
        }),
      staleTime: 0,
      retry: 1,
      refetchOnMount: "always",
    }
  );
